import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logout } from '../../../slices/authSlice';
import isoLanguages from '../../isoLanguages';
import '../../DashboardHome/brand-voice.css';
import './brandVoice.css';

const ICN = {
    x: (
        <svg viewBox="0 0 14 14" width="12" height="12" fill="none">
            <path d="M3.5 3.5l7 7M10.5 3.5l-7 7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
    ),
    plus: (
        <svg viewBox="0 0 14 14" width="13" height="13" fill="none">
            <path d="M7 2v10M2 7h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
    ),
    check: (
        <svg viewBox="0 0 14 14" width="12" height="12" fill="none">
            <path d="M2.5 7l3 3 6-7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    ),
    ban: (
        <svg viewBox="0 0 14 14" width="12" height="12" fill="none">
            <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.5" />
            <path d="M3.5 3.5l7 7" stroke="currentColor" strokeWidth="1.5" />
        </svg>
    ),
};

const SUPPORTED_ADDRESS_LANGS = ['DE', 'FR', 'ES', 'IT', 'PT', 'NL', 'JA'];

const LANGS = [
    ['DE', 'German'],
    ['FR', 'French'],
    ['ES', 'Spanish'],
    ['JA', 'Japanese'],
];

const ADDRESS_LABEL = {
    DE: { formal: 'Sie · formal', casual: 'du · informal' },
    FR: { formal: 'vous', casual: 'tu' },
    ES: { formal: 'usted', casual: 'tú' },
    JA: { formal: 'desu/masu', casual: 'plain form' },
    IT: { formal: 'Lei', casual: 'tu' },
    PT: { formal: 'você (formal)', casual: 'tu' },
    NL: { formal: 'u', casual: 'jij' },
};

const SAMPLES = {
    DE: [
        {
            src: 'Welcome back! Ready to pick up where you left off?',
            generic: 'Willkommen zurück! Bereit, dort weiterzumachen, wo Sie aufgehört haben?',
            formal: 'Schön, dass Sie wieder da sind. Möchten Sie dort weitermachen, wo Sie aufgehört haben?',
            casual: 'Schön, dass du wieder da bist. Lust, da weiterzumachen, wo du aufgehört hast?',
        },
        {
            src: 'Your free trial ends soon. Upgrade to keep your projects.',
            generic: 'Ihre kostenlose Testversion endet bald. Führen Sie ein Upgrade durch, um Ihre Projekte zu behalten.',
            formal: 'Ihre Testphase endet in Kürze — sichern Sie sich den vollen Zugang zu Ihren Projekten.',
            casual: 'Deine Testphase endet bald — sichere dir den vollen Zugang zu deinen Projekten.',
        },
        {
            src: 'Oops — something went wrong. Try again.',
            generic: 'Hoppla – etwas ist schiefgelaufen. Versuchen Sie es erneut.',
            formal: 'Da ist etwas schiefgelaufen. Bitte versuchen Sie es noch einmal.',
            casual: 'Da ist was schiefgelaufen. Versuch es einfach nochmal.',
        },
    ],
    FR: [
        {
            src: 'Welcome back! Ready to pick up where you left off?',
            generic: 'Bienvenue à nouveau ! Prêt à reprendre là où vous vous êtes arrêté ?',
            formal: 'Ravis de vous revoir. Souhaitez-vous reprendre là où vous vous êtes arrêté ?',
            casual: 'Content de te revoir ! Envie de reprendre là où tu t\'es arrêté ?',
        },
        {
            src: 'Your free trial ends soon. Upgrade to keep your projects.',
            generic: 'Votre essai gratuit se termine bientôt. Effectuez une mise à niveau pour conserver vos projets.',
            formal: 'Votre période d\'essai se termine bientôt — conservez l\'accès à tous vos projets.',
            casual: 'Ton essai se termine bientôt — garde l\'accès à tous tes projets.',
        },
        {
            src: 'Oops — something went wrong. Try again.',
            generic: 'Oups – une erreur s\'est produite. Veuillez réessayer.',
            formal: 'Une erreur s\'est produite. Veuillez réessayer.',
            casual: 'Aïe, ça n\'a pas marché. Réessaie.',
        },
    ],
    ES: [
        {
            src: 'Welcome back! Ready to pick up where you left off?',
            generic: '¡Bienvenido de nuevo! ¿Listo para continuar donde lo dejaste?',
            formal: 'Nos alegra verle de nuevo. ¿Desea continuar donde lo dejó?',
            casual: '¡Qué bueno verte! ¿Sigues donde lo dejaste?',
        },
    ],
    JA: [
        {
            src: 'Welcome back!',
            generic: 'おかえりなさい！',
            formal: 'お帰りなさいませ。',
            casual: 'おかえり。',
        },
    ],
};

const Spark = () => (
    <svg viewBox="0 0 16 16" width="14" height="14" fill="none">
        <path d="M8 2l1.3 3.4L13 6.6 9.7 8 8 11.5 6.3 8 3 6.6l3.4-1.2L8 2z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
    </svg>
);

const RefreshI = () => (
    <svg viewBox="0 0 14 14" width="13" height="13" fill="none">
        <path d="M11.5 3.5a5 5 0 10.8 4.2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M11.5 1.5v2.5H9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const TryVoice = ({ activeLang, voice }) => {
    const [idx, setIdx] = useState(0);
    const samples = SAMPLES[activeLang] || SAMPLES.DE;
    useEffect(() => setIdx(0), [activeLang]);
    const s = samples[idx] || samples[0];
    const addr = (voice.address || {})[activeLang] || 'formal';
    const onBrand = addr === 'casual' ? s.casual : s.formal;
    const warm = (voice.tone?.warmth || 50) >= 55;
    const langName = (LANGS.find((l) => l[0] === activeLang) || [])[1] || activeLang;
    const addrLabel = ADDRESS_LABEL[activeLang]?.[addr] || addr;

    return (
        <div className="bv-try">
            <div className="bv-try-h">
                <span className="bv-try-ic"><Spark /></span>
                <div>
                    <b>Try the voice</b>
                    <span>See how your profile shapes translations — compared to a generic engine.</span>
                </div>
                <div className="grow" />
                <button type="button" className="set-btn sm">
                    <RefreshI /> Regenerate
                </button>
            </div>

            <div className="bv-try-samples">
                {samples.map((sm, i) => (
                    <button
                        type="button"
                        key={i}
                        className={`bv-sample ${i === idx ? 'on' : ''}`}
                        onClick={() => setIdx(i)}
                    >
                        {sm.src}
                    </button>
                ))}
            </div>

            <div className="bv-try-out">
                <div className="bv-out generic">
                    <div className="bv-out-h"><span className="tag">Generic engine</span></div>
                    <div className="bv-out-t">{s.generic}</div>
                </div>
                <div className="bv-out brand">
                    <div className="bv-out-h">
                        <span className="tag accent"><Spark /> On-brand · Rush AI</span>
                    </div>
                    <div className="bv-out-t">{onBrand}</div>
                    <div className="bv-out-chips">
                        <span className="ac">{addrLabel}</span>
                        <span className="ac">{warm ? 'Warm tone' : 'Neutral tone'}</span>
                        <span className="ac">{langName} voice profile</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

const LangBar = ({ active, onChange }) => (
    <div className="bv-langbar">
        <span className="bv-langbar-l">Voice profile for</span>
        {LANGS.map(([code, name]) => (
            <button
                type="button"
                key={code}
                className={`bv-lang ${active === code ? 'on' : ''}`}
                onClick={() => onChange(code)}
            >
                <span className="code">{code}</span>{name}
            </button>
        ))}
        <button type="button" className="bv-lang add">+ Add language</button>
    </div>
);

const ToneSlider = ({ label, left, right, value, onChange }) => (
    <div className="bv-tone">
        <div className="bv-tone-h">
            <span className="lbl">{label}</span>
            <span className="val">{value}</span>
        </div>
        <div className="bv-tone-row">
            <span className="end">{left}</span>
            <input
                type="range"
                className="set-slider"
                min={0}
                max={100}
                value={value}
                onChange={(e) => onChange(Number(e.target.value))}
            />
            <span className="end">{right}</span>
        </div>
    </div>
);

const ChipInput = ({ items, onAdd, onRemove, placeholder }) => {
    const [v, setV] = useState('');
    const add = () => {
        const x = v.trim();
        if (x && !items.includes(x)) onAdd(x);
        setV('');
    };
    return (
        <div className="bv-chips">
            {items.map((it) => (
                <span key={it} className="bv-chip">
                    {it}
                    <button type="button" onClick={() => onRemove(it)} aria-label={`Remove ${it}`}>{ICN.x}</button>
                </span>
            ))}
            <input
                className="bv-chip-input"
                value={v}
                placeholder={placeholder}
                onChange={(e) => setV(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
            />
        </div>
    );
};

const PairList = ({ items, onAdd, onUpdate, onRemove, labels }) => {
    const [a, setA] = useState('');
    const [b, setB] = useState('');
    return (
        <div className="bv-pairs">
            {items.map((it, i) => (
                <div className="bv-pair" key={i}>
                    <input value={it[labels[0]]} onChange={(e) => onUpdate(i, labels[0], e.target.value)} />
                    <span className="arrow">→</span>
                    <input value={it[labels[1]]} onChange={(e) => onUpdate(i, labels[1], e.target.value)} />
                    <button type="button" onClick={() => onRemove(i)} aria-label="Remove">{ICN.x}</button>
                </div>
            ))}
            <div className="bv-pair">
                <input value={a} onChange={(e) => setA(e.target.value)} placeholder={labels[2]} />
                <span className="arrow">→</span>
                <input value={b} onChange={(e) => setB(e.target.value)} placeholder={labels[3]} />
                <button
                    type="button"
                    onClick={() => {
                        if (!a.trim() || !b.trim()) return;
                        onAdd({ [labels[0]]: a.trim(), [labels[1]]: b.trim() });
                        setA('');
                        setB('');
                    }}
                >
                    {ICN.plus}
                </button>
            </div>
        </div>
    );
};

const BrandVoice = () => {
    const token = useSelector((s) => s.auth.token);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [voice, setVoice] = useState(null);
    const [savedFlash, setSavedFlash] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [activeLang, setActiveLang] = useState('DE');

    useEffect(() => {
        if (!token) return;
        axios
            .get('/api/brand-voice', { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => {
                setVoice(r.data);
                setLoading(false);
            })
            .catch((err) => {
                if (err.response && err.response.status === 401) {
                    dispatch(logout());
                    navigate('/login');
                } else {
                    setError('Failed to load brand voice.');
                    setLoading(false);
                }
            });
    }, [token, dispatch, navigate]);

    const update = (patch) => setVoice((v) => ({ ...v, ...patch }));
    const updateTone = (k, val) => setVoice((v) => ({ ...v, tone: { ...v.tone, [k]: val } }));
    const updateAddress = (lang, val) => setVoice((v) => ({ ...v, address: { ...(v.address || {}), [lang]: val } }));

    const save = async () => {
        setSaving(true);
        try {
            const { _id, userId, __v, updatedAt, ...payload } = voice;
            await axios.put('/api/brand-voice', payload, { headers: { Authorization: `Bearer ${token}` } });
            setSavedFlash(true);
            setTimeout(() => setSavedFlash(false), 1500);
        } catch (e) {
            setError('Failed to save.');
        } finally {
            setSaving(false);
        }
    };

    const languageOptions = useMemo(() => {
        const codes = new Set(SUPPORTED_ADDRESS_LANGS);
        return isoLanguages.filter((l) => codes.has(l.code.toUpperCase())).map((l) => ({ code: l.code.toUpperCase(), name: l.name }));
    }, []);

    if (loading) return <div style={{ padding: 40, color: 'var(--ink-3)' }}>Loading brand voice…</div>;
    if (error) return <div style={{ padding: 40, color: 'crimson' }}>{error}</div>;
    if (!voice) return null;

    return (
        <>
            <div className="rs-page-title-row">
                <h1 className="rs-page-title">Brand voice</h1>
                <div className="rs-page-actions">
                    {savedFlash && <span style={{ color: 'var(--green)', fontWeight: 600, fontSize: 13 }}>Saved ✓</span>}
                    <button type="button" className="btn-acc" onClick={save} disabled={saving}>
                        {saving ? 'Saving…' : 'Save changes'}
                    </button>
                </div>
            </div>
            <p className="rs-page-sub">Define how your brand sounds in each language. Rush AI applies this profile to every draft.</p>

            <LangBar active={activeLang} onChange={setActiveLang} />

            <TryVoice activeLang={activeLang} voice={voice} />

            <div className="bv-grid" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 16 }}>
                <div className="rs-card">
                    <div className="rs-card-h"><h3>Voice summary</h3></div>
                    <textarea
                        value={voice.summary || ''}
                        onChange={(e) => update({ summary: e.target.value })}
                        placeholder="Describe how your brand should sound in one or two sentences…"
                        style={{
                            width: '100%',
                            minHeight: 80,
                            border: '1px solid var(--line)',
                            borderRadius: 10,
                            padding: 12,
                            background: 'var(--bg)',
                            color: 'var(--ink)',
                            fontFamily: 'inherit',
                            fontSize: 13.5,
                            outline: 'none',
                            resize: 'vertical',
                        }}
                    />
                </div>

                <div className="rs-card">
                    <div className="rs-card-h"><h3>Tone</h3></div>
                    <ToneSlider label="Formality" left="Casual" right="Formal" value={voice.tone.formality} onChange={(v) => updateTone('formality', v)} />
                    <ToneSlider label="Warmth" left="Cool" right="Warm" value={voice.tone.warmth} onChange={(v) => updateTone('warmth', v)} />
                    <ToneSlider label="Playfulness" left="Serious" right="Playful" value={voice.tone.playfulness} onChange={(v) => updateTone('playfulness', v)} />
                    <ToneSlider label="Energy" left="Calm" right="Energetic" value={voice.tone.energy} onChange={(v) => updateTone('energy', v)} />
                </div>

                <div className="rs-card">
                    <div className="rs-card-h"><h3>Address by language</h3></div>
                    <p style={{ color: 'var(--ink-3)', marginTop: 0, fontSize: 13 }}>
                        For languages with a formal/informal split (Sie vs du, vous vs tu, etc.).
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
                        {languageOptions.map((l) => {
                            const v = (voice.address || {})[l.code] || 'formal';
                            return (
                                <div key={l.code} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <span style={{ minWidth: 60, fontWeight: 600 }}>{l.code}</span>
                                    <span style={{ flex: 1, color: 'var(--ink-3)', fontSize: 12.5 }}>{l.name}</span>
                                    <div className="bv-seg">
                                        <button type="button" className={v === 'formal' ? 'on' : ''} onClick={() => updateAddress(l.code, 'formal')}>Formal</button>
                                        <button type="button" className={v === 'casual' ? 'on' : ''} onClick={() => updateAddress(l.code, 'casual')}>Casual</button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="rs-card">
                    <div className="rs-card-h"><h3>Reading level &amp; length</h3></div>
                    <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                        <div>
                            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Reading level</div>
                            <div className="bv-seg">
                                {['simple', 'standard', 'technical'].map((r) => (
                                    <button type="button" key={r} className={voice.reading === r ? 'on' : ''} onClick={() => update({ reading: r })}>
                                        {r}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div>
                            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Length</div>
                            <div className="bv-seg">
                                {['terse', 'balanced', 'expansive'].map((r) => (
                                    <button type="button" key={r} className={voice.length === r ? 'on' : ''} onClick={() => update({ length: r })}>
                                        {r}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="rs-card">
                    <div className="rs-card-h"><h3>Do not translate</h3></div>
                    <p style={{ color: 'var(--ink-3)', marginTop: 0, fontSize: 13 }}>
                        Brand names, product names, codes. These pass through unchanged in every language.
                    </p>
                    <ChipInput
                        items={voice.doNotTranslate || []}
                        onAdd={(x) => update({ doNotTranslate: [...(voice.doNotTranslate || []), x] })}
                        onRemove={(x) => update({ doNotTranslate: (voice.doNotTranslate || []).filter((i) => i !== x) })}
                        placeholder="Type and press Enter"
                    />
                </div>

                <div className="rs-card">
                    <div className="rs-card-h"><h3>Forbidden terms</h3></div>
                    <PairList
                        items={voice.forbidden || []}
                        onAdd={(p) => update({ forbidden: [...(voice.forbidden || []), p] })}
                        onUpdate={(i, k, val) => {
                            const next = [...(voice.forbidden || [])];
                            next[i] = { ...next[i], [k]: val };
                            update({ forbidden: next });
                        }}
                        onRemove={(i) => update({ forbidden: (voice.forbidden || []).filter((_, idx) => idx !== i) })}
                        labels={['term', 'reason', 'Term to avoid', 'Why / what to use instead']}
                    />
                </div>

                <div className="rs-card">
                    <div className="rs-card-h"><h3>Preferred terms</h3></div>
                    <p style={{ color: 'var(--ink-3)', marginTop: 0, fontSize: 13 }}>
                        Replace the left with the right.
                    </p>
                    <PairList
                        items={voice.preferred || []}
                        onAdd={(p) => update({ preferred: [...(voice.preferred || []), p] })}
                        onUpdate={(i, k, val) => {
                            const next = [...(voice.preferred || [])];
                            next[i] = { ...next[i], [k]: val };
                            update({ preferred: next });
                        }}
                        onRemove={(i) => update({ preferred: (voice.preferred || []).filter((_, idx) => idx !== i) })}
                        labels={['avoid', 'use', 'Avoid', 'Prefer']}
                    />
                </div>
            </div>
        </>
    );
};

export default BrandVoice;
